// FormTotalingTabs.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  FormControlLabel,
  Switch,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { AVAILABLE_FIELDS } from '../constants/fields';

const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={{
      p: 3,
      bgcolor: '#ffffff',
      borderRadius: '0 16px 16px 16px',
      border: '1px solid #e2e8f0',
      borderTop: 'none',
      minHeight: 280,
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
    }}
  >
    {value === index && children}
  </Box>
);

const FormTotalingTabs = ({ formTotaling, setFormTotaling }) => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  const totalingDescriptions = {
    approved: 'Sums the selected field where conditions are met (default: `approved` is checked).',
    completed: 'Sums the selected field where conditions are met (default: `completed` is checked).',
    pending: 'Sums the selected field where conditions are met (default: `recommend` or `required` is checked, but not `approved` or `completed`).',
    grand: 'Sums the selected field where conditions are met (default: `recommend`, `required`, or `approved` is checked).',
    parts: 'Sums the selected field for all items matching conditions (default: no conditions).',
    labor: 'Sums the selected field for all items matching conditions (default: no conditions).',
  };

  const addFilter = status => {
    setFormTotaling(prev => ({
      ...prev,
      [status]: {
        ...prev[status],
        filters: [...(prev[status].filters || []), { header: 'ro_status', operator: 'equals', value: '' }],
      },
    }));
  };

  const updateFilter = (status, index, field, value) => {
    setFormTotaling(prev => {
      const newFilters = [...(prev[status].filters || [])];
      newFilters[index] = { ...newFilters[index], [field]: value };
      return { ...prev, [status]: { ...prev[status], filters: newFilters } };
    });
  };

  const removeFilter = (status, index) => {
    setFormTotaling(prev => ({
      ...prev,
      [status]: {
        ...prev[status],
        filters: prev[status].filters.filter((_, i) => i !== index),
      },
    }));
  };

  const addCondition = status => {
    setFormTotaling(prev => ({
      ...prev,
      [status]: {
        ...prev[status],
        conditions: [...(prev[status].conditions || []), { header: 'estimate_type', operator: 'equals', value: '' }],
      },
    }));
  };

  const updateCondition = (status, index, field, value) => {
    setFormTotaling(prev => {
      const newConditions = [...(prev[status].conditions || [])];
      newConditions[index] = { ...newConditions[index], [field]: value };
      return { ...prev, [status]: { ...prev[status], conditions: newConditions } };
    });
  };

  const removeCondition = (status, index) => {
    setFormTotaling(prev => ({
      ...prev,
      [status]: {
        ...prev[status],
        conditions: prev[status].conditions.filter((_, i) => i !== index),
      },
    }));
  };

  const totalingTypes = ['approved', 'completed', 'pending', 'grand', 'parts', 'labor'];

  return (
    <Box sx={{ mt: 5, bgcolor: '#f8fafc', borderRadius: 4, p: 4, boxShadow: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
        Form Totaling
      </Typography>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          bgcolor: '#ffffff',
          borderRadius: '16px 16px 0 0',
          border: '1px solid #e2e8f0',
          '& .MuiTabs-indicator': {
            height: 4,
            borderRadius: '2px 2px 0 0',
            backgroundColor: '#3b82f6',
          },
        }}
      >
        {totalingTypes.map(status => (
          <Tab
            key={status}
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 56,
              borderRadius: '16px 16px 0 0',
              mx: 0.5,
              '&.Mui-selected': {
                color: '#1e40af',
                bgcolor: '#eff6ff',
              },
            }}
          />
        ))}
      </Tabs>

      {totalingTypes.map((status, index) => (
        <TabPanel key={status} value={tabValue} index={index}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Tooltip title={totalingDescriptions[status]}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formTotaling[status]?.enabled || false}
                    onChange={e =>
                      setFormTotaling(prev => ({
                        ...prev,
                        [status]: { ...prev[status], enabled: e.target.checked },
                      }))
                    }
                    color="primary"
                  />
                }
                label={`${status.charAt(0).toUpperCase() + status.slice(1)} Total`}
              />
            </Tooltip>
            {formTotaling[status]?.enabled && (
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Equation</InputLabel>
                <Select
                  value={formTotaling[status]?.equation || 'estimate_total'}
                  onChange={e =>
                    setFormTotaling(prev => ({
                      ...prev,
                      [status]: { ...prev[status], equation: e.target.value },
                    }))
                  }
                >
                  <MenuItem value="estimate_total">Estimate Total</MenuItem>
                  <MenuItem value="labor_total">Labor Total</MenuItem>
                  <MenuItem value="parts_total">Parts Total</MenuItem>
                  <MenuItem value="labor_hours * labor_rate">Labor Hours × Rate</MenuItem>
                  <MenuItem value="quantity * parts">Qty × Parts</MenuItem>
                  {AVAILABLE_FIELDS.filter(f => f.value.startsWith('custom_header_')).map(f => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {formTotaling[status]?.enabled && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Conditions
              </Typography>
              {(formTotaling[status].conditions || []).map((condition, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ width: 160 }}>
                    <InputLabel>Header</InputLabel>
                    <Select
                      value={condition.header}
                      onChange={e => updateCondition(status, i, 'header', e.target.value)}
                    >
                      {AVAILABLE_FIELDS.map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 130 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={condition.operator}
                      onChange={e => updateCondition(status, i, 'operator', e.target.value)}
                    >
                      <MenuItem value="equals">Equals</MenuItem>
                      <MenuItem value="less_than">Less Than</MenuItem>
                      <MenuItem value="greater_than">Greater Than</MenuItem>
                      <MenuItem value="or">Or (if previous)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Value"
                    value={condition.value}
                    onChange={e => updateCondition(status, i, 'value', e.target.value)}
                    sx={{ width: 160 }}
                  />
                  <IconButton size="small" color="error" onClick={() => removeCondition(status, i)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => addCondition(status)} sx={{ mt: 1 }}>
                Add Condition
              </Button>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                Filters
              </Typography>
              {(formTotaling[status].filters || []).map((filter, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ width: 160 }}>
                    <InputLabel>Header</InputLabel>
                    <Select
                      value={filter.header}
                      onChange={e => updateFilter(status, i, 'header', e.target.value)}
                    >
                      {AVAILABLE_FIELDS.map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 130 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={filter.operator}
                      onChange={e => updateFilter(status, i, 'operator', e.target.value)}
                    >
                      <MenuItem value="equals">Equals</MenuItem>
                      <MenuItem value="not_equals">Not Equals</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Value(s)"
                    value={filter.value}
                    onChange={e => updateFilter(status, i, 'value', e.target.value)}
                    placeholder="e.g., pending,true"
                    sx={{ width: 160 }}
                  />
                  <IconButton size="small" color="error" onClick={() => removeFilter(status, i)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => addFilter(status)} sx={{ mt: 1 }}>
                Add Filter
              </Button>
            </>
          )}
        </TabPanel>
      ))}
    </Box>
  );
};

export default FormTotalingTabs;